import { useEffect, useRef, useState } from 'react';
import { EventEmitter } from 'eventemitter3';

import { CreateDashboardModal, CreateDashboardModalProps } from './CreateDashboardModal.component';

interface ConfirmModalEventTypes {
  close: (confirm?: boolean) => void;
  open: () => void;
}

export type CreateDashboardModalPropsFromHook = Omit<CreateDashboardModalProps, 'isVisible'>;

export function useCreateDashboardModal() {
  const emitter = useRef(new EventEmitter<ConfirmModalEventTypes>());
  const Modal = useRef(function Modal(props: CreateDashboardModalPropsFromHook) {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
      const openListener = () => {
        setIsVisible(true);
      };

      const closeListener = () => {
        setIsVisible(false);
      };

      emitter.current.addListener('open', openListener);
      emitter.current.addListener('close', closeListener);

      return () => {
        emitter.current.removeListener('open', openListener);
        emitter.current.removeListener('close', closeListener);
      };
    }, []);

    if (!isVisible) {
      return null;
    }

    return (
      <CreateDashboardModal
        {...props}
        isVisible={isVisible}
        onSuccess={(...args) => {
          emitter.current.emit('close', true);
          props.onSuccess?.(...args);
        }}
        onCancel={() => {
          emitter.current.emit('close', false);
          props.onCancel?.();
        }}
        onCloseDialog={(e) => {
          emitter.current.emit('close', false);
          props.onCloseDialog?.(e);
        }}
      />
    );
  });

  const showModal = useRef(async () => {
    emitter.current.emit('open');

    return new Promise<boolean | undefined>((resolve) => {
      const closeListener = (confirm?: boolean) => {
        emitter.current.removeListener('close', closeListener);

        resolve(confirm);
      };

      emitter.current.addListener('close', closeListener);
    });
  });

  const hideModal = useRef(async () => {
    return new Promise((resolve) => {
      const closeListener = () => {
        emitter.current.removeListener('close', closeListener);

        resolve(undefined);
      };

      emitter.current.addListener('close', closeListener);
      emitter.current.emit('close');
    });
  });

  return {
    CreateDashboardModal: Modal.current,
    showCreateDashboardModal: showModal.current,
    hideCreateDashboardModal: hideModal.current,
  } as const;
}
